import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { Direction } from '@shared/types/entity';
import { TILE_SIZE } from '@shared/constants';
import {
  getFootstepMode,
  getDirectionalFootstepFrames,
  getAnimationFootstepFrames,
  getDirectionalFootstepAnimKey,
  getAnimationFootstepAnimKey,
  isFootstepSetEmpty,
  resolveFootstepId,
  FOOTSTEP_SHEET_KEY,
} from '../../sprites/FootstepSpriteManager';
import {
  SURFACE_FOOTSTEP_SOUNDS,
  WATER_FOOTSTEP_SOUND,
  DEFAULT_FOOTSTEP_SOUND,
} from '@shared/data/sounds';

const GRASS_FOOTSTEP_SOUND = SURFACE_FOOTSTEP_SOUNDS['grass'] ?? DEFAULT_FOOTSTEP_SOUND;

/**
 * Sistema de huellas cosméticas al caminar (footstep decals). Extraído de GameScene (god-object).
 * Genera sprites de huella/splash por paso, animándolos y desvaneciéndolos. Deriva sobre agua
 * navegable (ver docs/WATER_AND_BOATS.md). Accede a la escena vía `scene`. Cosmético (sin efecto
 * en gameplay). Ver docs/SPRITE_SYSTEM.md (FootstepSpriteManager).
 */
export class FootstepSystem {
  constructor(private scene: GameScene) {}

  /**
   * Asegura (lazy) la animación de Phaser de un footstep DIRECCIONAL (3 frames,
   * por dirección) y devuelve su key.
   */
  private ensureDirectionalFootstepAnim(footstepId: number, dir: Direction): string {
    const key = getDirectionalFootstepAnimKey(footstepId, dir);
    if (!this.scene.anims.exists(key)) {
      const frames = getDirectionalFootstepFrames(footstepId, dir).map((frame) => ({
        key: FOOTSTEP_SHEET_KEY,
        frame,
      }));
      this.scene.anims.create({
        key,
        frames,
        frameRate: (frames.length / GameScene.FOOTSTEP_ANIM_MS) * 1000,
        repeat: 0,
      });
    }
    return key;
  }

  /**
   * Asegura (lazy) la animación de Phaser de un footstep de ANIMACIÓN (12 frames,
   * sin dirección, ej. splash) y devuelve su key.
   */
  private ensureAnimationFootstepAnim(footstepId: number): string {
    const key = getAnimationFootstepAnimKey(footstepId);
    if (!this.scene.anims.exists(key)) {
      const frames = getAnimationFootstepFrames(footstepId).map((frame) => ({
        key: FOOTSTEP_SHEET_KEY,
        frame,
      }));
      this.scene.anims.create({
        key,
        frames,
        frameRate: GameScene.FOOTSTEP_SPLASH_FRAME_RATE,
        repeat: 0,
      });
    }
    return key;
  }

  /**
   * Marca la huella de UN paso de una entidad en el tile (fromTileX, fromTileY)
   * que deja atrás. Resuelve el set según la superficie (costa → splash) y luego:
   *
   *  - DIRECCIONAL (sets 1–4): 2 huellas (un pie a cada lado, perpendicular a la
   *    dirección), que persisten `durationMs` y se desvanecen. Usa el stat.
   *  - ANIMACIÓN (sets 5–7): 1 sprite con la anim completa de 12 frames que se
   *    destruye al terminar (no persiste, ignora el stat) — ej. splash de agua.
   *
   * Decal de piso puramente client-side (no toca server ni schema).
   */
  /**
   * Resuelve el id de sonido de paso para un tile: agua/navegando y costa → sonido de agua; caminos
   * (indexados en `footstepSurfaceSounds`) → su sonido; el resto (pasto/default) → grass. Ver
   * docs/SOUND_AND_MUSIC.md.
   */
  private resolveFootstepSound(tx: number, ty: number): string {
    if (this.scene.waterTiles?.has(`${tx},${ty}`)) return WATER_FOOTSTEP_SOUND;
    if (this.scene.isCoastTile(tx, ty)) return WATER_FOOTSTEP_SOUND;
    return this.scene.footstepSurfaceSounds.get(`${tx},${ty}`) ?? GRASS_FOOTSTEP_SOUND;
  }

  /**
   * Índice de variante DETERMINISTA para un paso: hash FNV-1a de (entityId, tile, dir). Todos los
   * clientes que observan la misma entidad dar el mismo paso computan el mismo índice → escuchan la
   * MISMA variante de pisada. Ver docs/SOUND_AND_MUSIC.md §Footsteps deterministas.
   */
  private footstepVariantIndex(entityId: string, tx: number, ty: number, dir: Direction): number {
    let h = 0x811c9dc5; // FNV-1a offset basis
    const str = `${entityId}:${tx}:${ty}:${dir}`;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h;
  }

  spawnFootstepForStep(
    fromTileX: number,
    fromTileY: number,
    dir: Direction,
    defaultFootstepId: number,
    durationMs: number,
    noWalkNoise: boolean = false,
    noFootsteps: boolean = false,
    entityId: string = ''
  ): void {
    // SFX de paso (eje AUDIO, separado del decal VISUAL): posicional en el tile del paso, salvo que
    // la entidad tenga el mod `noWalkNoise`. Se emite ANTES del early-return de agua/visual para que
    // el paso navegando conserve su sonido de agua y para que `noFootsteps` (visual) no lo silencie.
    // La variante se elige DETERMINISTA (hash) para que todos los observadores escuchen la misma.
    if (!noWalkNoise) {
      const variantIndex = this.footstepVariantIndex(entityId, fromTileX, fromTileY, dir);
      this.scene.audio.playAt(
        this.resolveFootstepSound(fromTileX, fromTileY),
        fromTileX,
        fromTileY,
        { variantIndex }
      );
    }

    // Decal visual: suprimido por `noFootsteps` (amuleto Paso Fantasma) o si falta el spritesheet.
    if (noFootsteps || !this.scene.textures.exists(FOOTSTEP_SHEET_KEY)) return;

    // Navegando NO se dejan huellas: si el origen del paso es agua NAVEGABLE
    // (solo se pisa con barco), el barco no marca el suelo. La costa caminable
    // (agua visual debajo) no está en waterTiles → conserva su splash.
    // Ver docs/WATER_AND_BOATS.md.
    if (this.scene.waterTiles?.has(`${fromTileX},${fromTileY}`)) return;

    const isCoast = this.scene.isCoastTile(fromTileX, fromTileY);
    const footstepId = resolveFootstepId(defaultFootstepId, isCoast);
    if (isFootstepSetEmpty(footstepId)) return;

    const centerX = fromTileX * TILE_SIZE + TILE_SIZE / 2;
    const centerY = fromTileY * TILE_SIZE + TILE_SIZE / 2;
    const mode = getFootstepMode(footstepId);

    if (mode === 'animation') {
      // Splash u otro efecto: una sola animación completa, se autodestruye al terminar.
      const key = this.ensureAnimationFootstepAnim(footstepId);
      const sprite = this.scene.add.sprite(centerX, centerY, FOOTSTEP_SHEET_KEY);
      sprite.setOrigin(0.5, 0.5);
      sprite.setDepth(GameScene.FOOTSTEP_GROUND_DEPTH);
      sprite.setAlpha(GameScene.FOOTSTEP_ALPHA);
      sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => sprite.destroy());
      sprite.play(key);
      return;
    }

    // DIRECCIONAL: dos pies. El eje perpendicular a la dirección define el lado
    // izq/der; para movimiento vertical (DOWN/UP) los pies se separan en X, para
    // movimiento horizontal (LEFT/RIGHT) en Y.
    const key = this.ensureDirectionalFootstepAnim(footstepId, dir);
    const vertical = dir === Direction.DOWN || dir === Direction.UP;
    const off = GameScene.FOOTSTEP_FOOT_OFFSET;
    for (const side of [-1, 1] as const) {
      const x = centerX + (vertical ? side * off : 0);
      const y = centerY + (vertical ? 0 : side * off);
      const sprite = this.scene.add.sprite(x, y, FOOTSTEP_SHEET_KEY);
      sprite.setOrigin(0.5, 0.5);
      sprite.setDepth(GameScene.FOOTSTEP_GROUND_DEPTH);
      sprite.setAlpha(GameScene.FOOTSTEP_ALPHA);
      sprite.setFlipX(side < 0); // espejo del pie izquierdo
      sprite.play(key);

      const fade = GameScene.FOOTSTEP_FADE_MS;
      const visible = Math.max(0, durationMs - fade);
      this.scene.tweens.add({
        targets: sprite,
        alpha: 0,
        delay: visible,
        duration: fade,
        onComplete: () => sprite.destroy(),
      });
    }
  }

  /**
   * Huella de una entidad REMOTA (otro jugador o NPC) que se observó cambiar de
   * tile de (fromX,fromY) a (toX,toY). Deriva la dirección del delta y spawnea la
   * huella en el tile origen, solo si el avance fue de exactamente 1 tile (evita
   * snaps/teleports). La huella sale en el tile que la entidad dejó atrás.
   */
  spawnRemoteEntityFootstep(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    defaultFootstepId: number,
    durationMs: number,
    noWalkNoise: boolean = false,
    noFootsteps: boolean = false,
    entityId: string = ''
  ): void {
    const dx = toX - fromX;
    const dy = toY - fromY;
    if (Math.abs(dx) + Math.abs(dy) !== 1) return; // no fue un paso simple
    let dir: Direction;
    if (dx > 0) dir = Direction.RIGHT;
    else if (dx < 0) dir = Direction.LEFT;
    else if (dy > 0) dir = Direction.DOWN;
    else dir = Direction.UP;
    this.spawnFootstepForStep(fromX, fromY, dir, defaultFootstepId, durationMs, noWalkNoise, noFootsteps, entityId);
  }
}
